import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
} from '@material-ui/core';
import { Block } from '@klp/design/index';
import { useCurrentUser } from '@klp/auth';
import { Portfolio } from '../classes/Portfolio';
import { usePortfolio } from '../store';
import { useGetPortfolioListQuery } from '../services/rtkApi';
import { filterPortfolioList } from '../utils';
import type { PortfolioRoleFilter } from '../utils';

interface IPortfolioSelectorProps {
    filter?: PortfolioRoleFilter;
    // Translation key for the label
    label?: string;
    // Translation key for the option that selects all portfolios
    allLabel?: string;
    disabled?: boolean;
}

const emptyPortfolio = Portfolio.empty();

const useStyles = makeStyles((theme) => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    selectAdornment: {
        marginRight: theme.spacing(3),
    },
}));

const getSelectedValue = (
    portfolio: Portfolio | undefined,
    filteredPortfolio: Array<Portfolio>,
    allText: string,
) => {
    return portfolio &&
        filteredPortfolio.filter((el) => el.brukerId === portfolio.brukerId)
            .length !== 0
        ? portfolio.brukerId
        : allText;
};

const renderMenuItem = (entry: Portfolio, index: number) => (
    <MenuItem key={`${entry.brukerId}-${index}`} value={entry.brukerId}>
        {entry.fornavn} {entry.etternavn}
    </MenuItem>
);

export const PortfolioSelector: React.FC<IPortfolioSelectorProps> = ({
    filter,
    label = 'portfolio.select',
    allLabel = 'portfolio.all',
    disabled,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { portfolio, selectPortfolio, resetPortfolioField } = usePortfolio();
    const { currentUser } = useCurrentUser();

    // The list is cached, and shared by every selector
    const { data: portfolioResponse, isLoading: portfolioListLoading } =
        useGetPortfolioListQuery();

    const portfolioList = portfolioResponse?.portfolioList;

    const filteredPortfolio = useMemo(
        () => filterPortfolioList(portfolioList, filter),
        [filter, portfolioList],
    );

    const currentPortfolio = useMemo(
        () =>
            currentUser
                ? filteredPortfolio.find(
                      (entry) => entry.brukerId === currentUser.brukerId,
                  )
                : undefined,
        [currentUser, filteredPortfolio],
    );

    // Runs when the list is available, also when it comes from the cache
    useEffect(() => {
        if (!portfolioList) return;

        // Only preselect when nothing is stored, so that «all» stays selected
        if (Portfolio.storedSelectedPortfolio === undefined) {
            if (!currentUser) return;

            if (currentPortfolio) {
                selectPortfolio(currentPortfolio);
            } else {
                resetPortfolioField();
            }
            return;
        }

        // A portfolio selected with other roles shouldn't be sent to the
        // list APIs when it isn't in this list
        const selectedIsMissing =
            portfolio &&
            portfolio.brukerId !== Portfolio.emptyId &&
            !filteredPortfolio.some(
                (entry) => entry.brukerId === portfolio.brukerId,
            );

        if (selectedIsMissing) {
            resetPortfolioField();
        }
    }, [
        portfolioList,
        currentUser,
        currentPortfolio,
        filteredPortfolio,
        portfolio,
        selectPortfolio,
        resetPortfolioField,
    ]);

    const currentUserMenuItem = currentPortfolio
        ? renderMenuItem(currentPortfolio, -1)
        : undefined;

    const handlePortfolioSelect = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const brukerId = event.target.value;

        const selectedPortfolio =
            portfolioList?.find((entry) => entry.brukerId === brukerId) ??
            emptyPortfolio;

        selectPortfolio(selectedPortfolio);
    };

    return (
        <Block marginBottom="tiny">
            <FormControl
                className={classes.selector}
                data-testid="PortfolioSelector"
                disabled={disabled}
            >
                <InputLabel id="PortfolioSelect__Label">{t(label)}</InputLabel>
                <Select
                    labelId="PortfolioSelect__Label"
                    // Only used by the outlined variant, to make room for the label in the border
                    label={t(label)}
                    id="PortfolioSelect"
                    value={getSelectedValue(
                        portfolio,
                        filteredPortfolio,
                        t(allLabel) as string,
                    )}
                    onChange={handlePortfolioSelect}
                    data-testid="PortfolioSelect"
                    disabled={disabled || portfolioListLoading}
                    endAdornment={
                        portfolioListLoading && (
                            <InputAdornment
                                className={classes.selectAdornment}
                                position="end"
                            >
                                <CircularProgress size={20} />
                            </InputAdornment>
                        )
                    }
                >
                    {currentUserMenuItem}
                    <MenuItem
                        key={t(allLabel)}
                        value={t(allLabel) as string}
                        className="menuItem"
                    >
                        {t(allLabel)}
                    </MenuItem>
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <MenuItem key="divider" value="divider" disabled={true}>
                        - - - - - - - - - -
                    </MenuItem>
                    {filteredPortfolio.map((entry, index) =>
                        renderMenuItem(entry, index),
                    )}
                </Select>
            </FormControl>
        </Block>
    );
};
